
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { uploadFileToAssistant } from "../utils/fileUtils";
import { getOpenAIClient } from "@/lib/openai";
import type { ExistingFile } from "../types";

export const ALLOWED_FILE_TYPES = [
  'text/x-python',
  'text/javascript',
  'text/markdown',
  'text/plain',
  'application/json',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/html',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

export const useFileUpload = (
  assistantId: string | undefined,
  vectorStoreId: string | undefined,
  onVectorStoreCreated: ((id: string) => void) | undefined,
  setExistingFiles: (files: ExistingFile[] | ((prev: ExistingFile[]) => ExistingFile[])) => void
) => {
  const [isUploadingNewFile, setIsUploadingNewFile] = useState(false);

  const createNewVectorStore = async (name: string): Promise<string> => {
    console.log('Creating new vector store...');
    try {
      const openai = await getOpenAIClient();
      
      // Create vector store using fetch API instead of client library
      const response = await fetch('https://api.openai.com/v1/vector_stores', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openai.apiKey}`,
          'Content-Type': 'application/json',
          'OpenAI-Beta': 'assistants=v2'
        },
        body: JSON.stringify({
          name: name || 'Assistant Knowledge Base',
          expires_after: {
            anchor: "last_active_at",
            days: 30 // Set a sensible default expiration
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to create vector store: ${response.statusText}`);
      }

      const vectorStore = await response.json();
      console.log('Vector store created in OpenAI:', vectorStore);
      
      // Save to our database
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }
      
      const { data, error } = await supabase
        .from('vector_stores')
        .insert({
          user_id: session.user.id,
          vector_store_id: vectorStore.id,
          name: name || 'Assistant Knowledge Base',
          openai_vector_store_id: vectorStore.id
        })
        .select()
        .single();
        
      if (error) throw error;
      
      return data.id;
    } catch (error) {
      console.error('Error creating vector store:', error);
      throw error;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files) as File[];
    const validFiles = newFiles.filter(file => ALLOWED_FILE_TYPES.includes(file.type));
    
    if (validFiles.length !== newFiles.length) {
      toast.error("Some files were not added because they are not supported. Supported files include: Python, JavaScript, Markdown, Text, JSON, PDF, Word, and PowerPoint files.");
    }

    if (validFiles.length === 0) return;

    setIsUploadingNewFile(true);
    try {
      let currentVectorStoreId = vectorStoreId;
      
      if (!currentVectorStoreId) {
        // Create a new vector store
        currentVectorStoreId = await createNewVectorStore('Assistant Knowledge Base');
        console.log('New vector store created:', currentVectorStoreId);
        onVectorStoreCreated?.(currentVectorStoreId);
      }

      // Upload files in parallel but wait for all to complete
      const uploadPromises = validFiles.map(file => 
        uploadFileToAssistant(
          file, 
          currentVectorStoreId!, 
          assistantId
        )
      );
      
      const uploadedFiles = await Promise.all(uploadPromises);
      
      // Update the UI
      setExistingFiles(prev => [...prev, ...uploadedFiles]);
      
      // If we have an assistant ID, make sure the vector store is attached
      if (assistantId) {
        try {
          const openai = await getOpenAIClient();
          const { data: vectorStore } = await supabase
            .from('vector_stores')
            .select('openai_vector_store_id')
            .eq('id', currentVectorStoreId)
            .single();
            
          if (vectorStore) {
            // Update the assistant with the vector store
            await openai.beta.assistants.update(
              assistantId,
              {
                tools: [{ type: "file_search" }],
                tool_resources: {
                  file_search: {
                    vector_store_ids: [vectorStore.openai_vector_store_id]
                  }
                }
              }
            );
            console.log('Vector store attached to assistant:', assistantId);
          }
        } catch (error) {
          console.error('Error attaching vector store to assistant:', error);
          // Continue anyway since the files were uploaded
        }
      }
      
      toast.success(`${uploadedFiles.length} file${uploadedFiles.length === 1 ? '' : 's'} uploaded successfully`);
    } catch (error: any) {
      console.error('Error uploading files:', error);
      toast.error(error.message || "Failed to upload files. Please try again.");
    } finally {
      setIsUploadingNewFile(false);
      if (e.target.value) e.target.value = '';
    }
  };

  return {
    isUploadingNewFile,
    handleFileChange,
    ALLOWED_FILE_TYPES
  };
};
